/**
 * useIsPortrait — reactive portrait-orientation check.
 *
 * Orientation is not width: a 768px-wide viewport is the overlay tier whether
 * it is an upright tablet or a landscape phone, and those two want opposite
 * demo-stage geometry (see {@link ../components/websiteAgentLayout#demoStageStacked}).
 * `useViewportTier` cannot tell them apart, so this is the second axis.
 *
 * Same discipline as {@link ./useIsMobile}: reach for it when the branch has to
 * happen in JS/TS. A purely visual change belongs in a CSS `@media` rule.
 */

// The pristine matchMedia, for the same reason useIsMobile takes it: the
// sidebar-compat viewport facade rewrites queries for the HOST page, and our
// own layout must read the real viewport (see ../sdk/native-viewport).
import { hasMatchMedia, realMatchMedia } from '../sdk/native-viewport';
import { useMediaQueryFlag } from './useMediaQueryFlag';

/** The media query this hook is defined by. */
export const PORTRAIT_MEDIA_QUERY = '(orientation: portrait)';

/**
 * Synchronous, one-shot read of the current match. Safe to call during
 * render — returns `false` when there's no `window`/`matchMedia` (SSR or a
 * non-DOM test env), which is the landscape/desktop default every caller
 * wants. Also the hook's lazy initial state, so the first paint is correct.
 */
export function matchesPortrait(): boolean {
  if (!hasMatchMedia()) return false;
  return !!realMatchMedia(PORTRAIT_MEDIA_QUERY)?.matches;
}

/**
 * Returns `true` while the viewport is taller than it is wide, and updates
 * live as the device rotates.
 */
export function useIsPortrait(): boolean {
  return useMediaQueryFlag(PORTRAIT_MEDIA_QUERY, matchesPortrait);
}
